/**
 * BookingNavigation - Navegación visual del wizard de agendamiento
 * Muestra progreso y permite saltar entre pasos completados
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBooking } from './BookingContext';
import { BookingStep } from './BookingWizard';

interface BookingNavigationProps {
  steps: { id: BookingStep; title: string; icon: string }[];
  currentStep: BookingStep;
  currentStepIndex: number;
  onStepClick: (step: BookingStep) => void;
}

const BookingNavigation: React.FC<BookingNavigationProps> = ({
  steps,
  currentStep: _currentStep,
  currentStepIndex,
  onStepClick
}) => {
  const { isStepComplete } = useBooking();

  const handleStepClick = (step: BookingStep, index: number) => {
    // Solo permitir navegar a pasos anteriores o el actual
    if (index <= currentStepIndex) {
      onStepClick(step);
    }
  };

  const getStepStatus = (step: BookingStep, index: number) => {
    if (index < currentStepIndex) {
      return isStepComplete(step) ? 'completed' : 'skipped';
    } else if (index === currentStepIndex) {
      return 'current';
    } else {
      return 'pending';
    }
  };

  const getStepStatusText = (status: string): string => {
    switch (status) {
      case 'completed': return 'Completado';
      case 'current': return 'En progreso';
      case 'pending': return 'Pendiente';
      default: return '';
    }
  };

  return (
    <nav className="booking-stepper" aria-label="Progreso del agendamiento">
      {/* Línea de progreso continua */}
      <div className="stepper-track">
        <motion.div
          className="stepper-fill"
          initial={{ width: '0%' }}
          animate={{
            width: `${((currentStepIndex + 1) / steps.length) * 100}%`
          }}
          transition={{
            duration: 0.5,
            ease: [0.23, 1, 0.32, 1]
          }}
        />
      </div>

      {steps.map((step, index) => {
        const status = getStepStatus(step.id, index);
        const isClickable = index <= currentStepIndex;

        return (
          <motion.button
            key={step.id}
            className={`stepper-step ${status}`}
            onClick={() => handleStepClick(step.id, index)}
            disabled={!isClickable}
            aria-current={status === 'current' ? 'step' : undefined}
            aria-label={`Paso ${index + 1}: ${step.title} — ${getStepStatusText(status)}`}
            whileHover={isClickable ? { scale: 1.08 } : {}}
            whileTap={isClickable ? { scale: 0.94 } : {}}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.25,
              delay: index * 0.08,
              type: 'spring',
              stiffness: 400
            }}
          >
            <motion.div
              className={`stepper-circle ${status}`}
              animate={
                status === 'current'
                  ? { scale: [1, 1.08, 1] }
                  : { scale: 1 }
              }
              transition={{
                duration: 1.8,
                repeat: status === 'current' ? Infinity : 0,
                repeatType: 'loop'
              }}
            >
              <AnimatePresence mode="wait">
                {status === 'completed' ? (
                  <motion.i
                    key="check"
                    className="fas fa-check"
                    aria-hidden="true"
                    initial={{ scale: 0, rotate: -90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.35, type: 'spring', stiffness: 400 }}
                  />
                ) : (
                  <motion.span
                    key="number"
                    className="step-number"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    {index + 1}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Etiqueta del paso — solo desktop, oculta en mobile */}
            <span className="stepper-label" aria-hidden="true">{step.title}</span>
          </motion.button>
        );
      })}
    </nav>
  );
};

export default BookingNavigation;